// AHP decision support — Batch 14.
// Sensitivity-slider UX: drag a weight, watch parcels re-rank live.
// Brian's pre-positioning note: "the sensitivity slider is the artifact.
// Move 'speed weight' up 10%, watch the ranking re-sort live. That's
// the moment AHP becomes visceral rather than abstract."
// Brain: [skills/land-intelligence].
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Match the backend's CONSISTENCY_RATIO_THRESHOLD. Updated through the
// /ahp/criteria endpoint at mount so a backend change picks up here.
const DEFAULT_CR_THRESHOLD = 0.10;

const STAGE_OPTIONS = [
  { value: '', label: 'All stages' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'screening', label: 'Screening' },
  { value: 'diligence', label: 'Diligence' },
  { value: 'discovered', label: 'Discovered' },
];

export default function LandAHP() {
  const [criteria, setCriteria] = useState([]);
  const [crThreshold, setCrThreshold] = useState(DEFAULT_CR_THRESHOLD);
  const [weights, setWeights] = useState({});
  const [stage, setStage] = useState('qualified');
  const [ranked, setRanked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [error, setError] = useState(null);
  const [profileName, setProfileName] = useState('brians-default');
  const [profileNotes, setProfileNotes] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  // Initial load — fetch criteria + saved profile (if any)
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cr = await fetch(`${API_BASE}/land/ahp/criteria`, { headers: authHeaders() });
        if (!cr.ok) throw new Error(`criteria: ${cr.status}`);
        const cj = await cr.json();
        setCriteria(cj.criteria || []);
        setCrThreshold(cj.consistency_ratio_threshold ?? DEFAULT_CR_THRESHOLD);

        // Load most-recent profile, or fall back to equal weights
        const pr = await fetch(`${API_BASE}/land/ahp/profile`, { headers: authHeaders() });
        if (pr.ok) {
          const pj = await pr.json();
          setWeights(pj.weights || {});
          setProfileName(pj.name || 'brians-default');
          setProfileNotes(pj.notes || '');
          setSavedAt(pj.updated_at || null);
        } else {
          // 404 expected on fresh deploy — initialize to equal weights
          const n = (cj.criteria || []).length;
          const equal = Object.fromEntries((cj.criteria || []).map((c) => [c.id, 1.0 / n]));
          setWeights(equal);
        }
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Re-score whenever weights change (debounced via the computed
  // `weightsKey` so contiguous slider drags don't fire N requests
  // per second).
  const weightsKey = useMemo(() => {
    return Object.entries(weights)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v.toFixed(3)}`)
      .join('|');
  }, [weights]);

  const score = useCallback(async () => {
    if (Object.keys(weights).length === 0) return;
    setScoring(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/land/ahp/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          weights,
          pipeline_stage: stage || null,
        }),
      });
      if (!resp.ok) {
        const detail = (await resp.json().catch(() => ({})))?.detail || resp.statusText;
        throw new Error(detail);
      }
      const body = await resp.json();
      setRanked(body.ranked_parcels || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setScoring(false);
    }
  }, [weights, stage]);

  // Debounce: 200ms after last weight change
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(score, 200);
    return () => clearTimeout(t);
  }, [weightsKey, stage, loading, score]);

  const updateWeight = (id, value) => {
    // Treat slider as 0-1 directly; the backend renormalizes.
    setWeights((w) => ({ ...w, [id]: value }));
  };

  const resetEqual = () => {
    if (criteria.length === 0) return;
    const eq = 1.0 / criteria.length;
    setWeights(Object.fromEntries(criteria.map((c) => [c.id, eq])));
  };

  const saveProfile = async () => {
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/land/ahp/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: profileName,
          weights,
          notes: profileNotes || null,
        }),
      });
      if (!resp.ok) {
        const detail = (await resp.json().catch(() => ({})))?.detail || resp.statusText;
        throw new Error(detail);
      }
      const body = await resp.json();
      setSavedAt(body.updated_at);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  // Slider weights are renormalized server-side; show the local-frame
  // sum so operators can see when it drifts and reset if desired.
  const localSum = Object.values(weights).reduce((a, b) => a + b, 0);

  if (loading) {
    return <div className="p-10 text-center text-sm text-zinc-500">Loading AHP…</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50 p-6 lg:p-10">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex items-center gap-2 text-sm text-zinc-500">
          <Link to="/land-intelligence/pipeline" className="hover:text-zinc-900">
            ← Pipeline
          </Link>
        </div>

        <header className="mb-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
            Land Intelligence — AHP decision support
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-900">
            Sensitivity slider
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-600">
            Drag a weight slider — watch parcels re-rank live. Each slider
            is one criterion (speed, cost, revenue, infrastructure). Move
            speed up 10%, see what jumps. Save the profile when you find
            the weighting that matches your operating preference.
          </p>
        </header>

        {error && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 className="text-lg font-bold text-zinc-900">Weights</h2>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span>sum: {localSum.toFixed(2)}</span>
                <button
                  type="button"
                  onClick={resetEqual}
                  className="rounded-lg border border-zinc-300 bg-white px-2 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-100"
                >
                  Reset equal
                </button>
              </div>
            </div>

            <ul className="space-y-4">
              {criteria.map((c) => {
                const w = weights[c.id] ?? 0;
                return (
                  <li key={c.id}>
                    <div className="flex items-baseline justify-between gap-2">
                      <label className="text-sm font-semibold text-zinc-900">
                        {c.label}
                      </label>
                      <span className="font-mono text-xs text-zinc-700">
                        {w.toFixed(2)}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={w}
                      onChange={(e) => updateWeight(c.id, Number(e.target.value))}
                      className="mt-1 w-full"
                    />
                    <p className="mt-1 text-xs text-zinc-500">{c.description}</p>
                  </li>
                );
              })}
            </ul>

            <hr className="my-5 border-zinc-200" />

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs font-semibold text-zinc-700">
                  Pipeline stage
                  <select
                    value={stage}
                    onChange={(e) => setStage(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm"
                  >
                    {STAGE_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                  Save profile
                </p>
                <input
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Profile name"
                  className="mt-2 block w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm"
                />
                <textarea
                  value={profileNotes}
                  onChange={(e) => setProfileNotes(e.target.value)}
                  placeholder="Notes (optional)"
                  rows={2}
                  className="mt-2 block w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm"
                />
                <div className="mt-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={saveProfile}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
                  >
                    Save profile
                  </button>
                  {savedAt && (
                    <span className="text-xs text-zinc-500">
                      saved {new Date(savedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-lg font-bold text-zinc-900">
                Ranked — {ranked.length} parcels
              </h2>
              {scoring && (
                <span className="text-xs text-zinc-500">scoring…</span>
              )}
            </div>

            {ranked.length === 0 ? (
              <p className="text-sm text-zinc-500">
                No parcels match the current filter (stage = {stage || 'all'}).
              </p>
            ) : (
              <ol className="space-y-2">
                {ranked.slice(0, 25).map((r) => (
                  <li
                    key={r.parcel_id}
                    className="rounded-xl border border-zinc-200 bg-white p-3"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex items-baseline gap-3">
                        <span className="font-mono text-xs font-bold text-zinc-500">
                          #{r.rank}
                        </span>
                        <Link
                          to={`/land-intelligence/parcel/${encodeURIComponent(r.parcel_id)}`}
                          className="text-sm font-semibold text-zinc-900 hover:text-emerald-700"
                        >
                          {r.parcel_id}
                        </Link>
                      </div>
                      <span className="font-mono text-xs font-bold text-emerald-700">
                        {(r.composite_score * 100).toFixed(1)}
                      </span>
                    </div>
                    <div className="mt-1 grid grid-cols-4 gap-2 text-xs text-zinc-600">
                      {Object.entries(r.criterion_scores).map(([cid, score]) => {
                        const c = criteria.find((x) => x.id === cid);
                        return (
                          <div key={cid} title={c?.label || cid}>
                            <span className="font-semibold">{cid.replace(/_/g, ' ')}</span>
                            : <span className="font-mono">{(score * 100).toFixed(0)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <p className="mt-6 text-xs text-zinc-500">
          Slider weights are renormalized to sum 1.0 by the scoring engine.
          Local sum drift (e.g. 0.94) is fine — drag where it makes sense,
          the math handles the rest. Pairwise comparison mode (with CR ≤{' '}
          {crThreshold} consistency check) is available via the API for advanced AHP usage.
        </p>
      </div>
    </div>
  );
}
